import { defineStore } from 'pinia'   
import { computed, ref, Ref } from 'vue'
import { useUserStore } from './user'
import { useSessionStore } from './session'

import { DEBUG, DEBUG_WARN, UnitApi } from '@/util'
import { IUnitDTO } from '@/types/IUnitDTO'

/** store of localized units of measurement */
export const useUnitStore = defineStore('unitStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _language: Ref<string> = ref('');
    const _unitData: Ref<null | Map<string, IUnitDTO>> = ref(null);

    /********************************************************************************************************************
     GETTERS
    ********************************************************************************************************************/
    /** currently active language */
    const language = computed(() => _language.value);

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    /** Sets the currently active language and automatically reloads data */
    function setLanguage(language: string) {
        const sessionStore = useSessionStore()

        sessionStore.isAuthenticated()
            .then((isAuthenticated) => {
                if (isAuthenticated) {
                    if (language != _language.value) {
                        _language.value = language
                        loadUnitData()
                    }
                }
            })
    }

    /** Forces reload of the data of the currently set language */
    function loadUnitData() {
        DEBUG('UnitStore: Fetching unit data...');
        const api = new UnitApi();
        api.get(_language.value)
            .then(response => _unitData.value = new Map<string, IUnitDTO>(
                response.data.map(u => [u.unitOfMeasure.toLowerCase(), u])
            ))
            .catch(error => DEBUG_WARN('UnitStore: Failed to load unit data:', error));
    }

    /** Returns the localized unit data related to the specified unit of measurement
        @param unitOfMeasure: The unit of measurement to look up
        */
    function get(unitOfMeasure: string | undefined) {
        if (unitOfMeasure == null) return undefined;
        return _unitData.value?.get(unitOfMeasure.toLowerCase());
    }

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/
    // Setup dependency on user store
    const userStore = useUserStore();

    userStore.$subscribe((mutation, state) => {
        setLanguage(state._language)
    });

    if (!language.value) {
        setLanguage(userStore.language);
    }

    return { language, setLanguage, loadUnitData, get }
})